import { Printer } from 'lucide-react'
import { useState } from 'react'
import { useCurrentBusiness } from '~/entities/business/model/use-business'
import { useListing } from '~/entities/listing/model/use-listings'
import { FlyerGeneratorDialog } from '~/features/ai-studio/components'
import type { FlyerLocale } from '~/features/events/checkin-flyer/flyer-content'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import type { Listing } from '~/shared/types'
import { Button } from '~/shared/ui/button'
import { Card } from '~/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { InfoTooltip } from '~/shared/ui/info-tooltip'
import { SegmentedControl } from '~/shared/ui/segmented-control'
import { Skeleton } from '~/shared/ui/skeleton'
import { useFlyerPrint } from '../hooks/use-flyer-print'
import { type RenderSheet, usePlaceFlyerSheets } from '../hooks/use-place-flyer-sheets'
import type { FlyerTone, PrintSize } from '../lib/place-flyer-content'

const SIZE_LABELS: Record<PrintSize, () => string> = {
  a4: m.placeFlyersSizeA4,
  a5: m.placeFlyersSizeA5,
  a6: m.placeFlyersSizeA6,
}

function appFlyerLocale(): FlyerLocale {
  return getLocale() === 'en' ? 'en' : 'de'
}

export function PlaceFlyersCard({ listingId }: { listingId: string }) {
  const { data: business } = useCurrentBusiness()
  const { data: listing } = useListing(business?.id ?? '', listingId)

  if (!business || !listing) return null

  const isDraft = listing.isDraft || listing.status === 'Draft'

  return (
    <Card className="flex w-full flex-col gap-3 p-4">
      <div className="flex items-center gap-1">
        <h3 className="font-semibold font-serif">{m.placeFlyersTitle()}</h3>
        <InfoTooltip label={m.placeFlyersTitle()}>{m.placeFlyersDescription()}</InfoTooltip>
      </div>
      {isDraft ? (
        <p className="text-muted-foreground text-sm">{m.publishBeforeSharing()}</p>
      ) : (
        <FlyerThumbnails businessId={business.id} listing={listing} />
      )}
    </Card>
  )
}

function FlyerThumbnails({ businessId, listing }: { businessId: string; listing: Listing }) {
  const { sheets, loading } = usePlaceFlyerSheets(businessId, listing, 'white', appFlyerLocale())

  return (
    <Dialog>
      <div className="flex items-end gap-3">
        {sheets.map((sheet) => (
          <DialogTrigger key={sheet.key} asChild>
            <button
              type="button"
              aria-label={m.placeFlyersOpen({ name: sheet.name })}
              className="flex w-20 flex-col items-center gap-1.5 rounded-md text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="block w-full rounded-md bg-muted p-1.5 transition-colors hover:bg-accent motion-reduce:transition-none">
                {sheet.render('pointer-events-none aspect-[148/210] w-full rounded-sm shadow')}
              </span>
              <span className="w-full truncate text-center text-muted-foreground">
                {sheet.name}
              </span>
            </button>
          </DialogTrigger>
        ))}
        {loading ? <Skeleton className="aspect-[148/210] w-20 rounded-md" /> : null}
      </div>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{m.placeFlyersTitle()}</DialogTitle>
          <DialogDescription>{m.placeFlyersDescription()}</DialogDescription>
        </DialogHeader>
        <PlaceFlyers businessId={businessId} listing={listing} />
      </DialogContent>
    </Dialog>
  )
}

function PlaceFlyers({ businessId, listing }: { businessId: string; listing: Listing }) {
  const [size, setSize] = useState<PrintSize>('a5')
  const [tone, setTone] = useState<FlyerTone>('white')
  const [locale, setLocale] = useState<FlyerLocale>(appFlyerLocale)
  const { sheets, shareUrl, loading } = usePlaceFlyerSheets(businessId, listing, tone, locale)
  const { print, portal } = useFlyerPrint()

  return (
    <>
      <div className="flex flex-wrap gap-2">
        <SegmentedControl
          label={m.placeFlyersSize()}
          value={size}
          onChange={setSize}
          options={(['a4', 'a5', 'a6'] as const).map((value) => ({
            value,
            label: SIZE_LABELS[value](),
          }))}
        />
        <SegmentedControl
          label={m.placeFlyersStyle()}
          value={tone}
          onChange={setTone}
          options={[
            { value: 'white', label: m.placeFlyersStyleWhite() },
            { value: 'color', label: m.placeFlyersStyleColor() },
          ]}
        />
        <SegmentedControl
          label={m.placeFlyersLanguage()}
          value={locale}
          onChange={setLocale}
          options={[
            { value: 'de', label: m.placeFlyersLanguageDe() },
            { value: 'en', label: m.placeFlyersLanguageEn() },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {sheets.map((sheet) => (
          <FlyerTile
            key={sheet.key}
            name={sheet.name}
            hint={sheet.hint}
            size={size}
            render={sheet.render}
            onPrint={() => print(size, sheet.render('size-full'))}
          />
        ))}
        {loading ? <Skeleton className="aspect-[148/210] w-full rounded-lg" /> : null}
      </div>

      <div className="flex justify-start">
        <FlyerGeneratorDialog
          qrData={shareUrl}
          label={listing.name}
          coverUrl={listing.coverImageUrl}
          images={listing.images}
          description={listing.description}
          triggerClassName="px-0"
          triggerVariant="link"
          triggerLabel={m.placeFlyersCustom()}
        />
      </div>
      {portal}
    </>
  )
}

function FlyerTile({
  name,
  hint,
  size,
  render,
  onPrint,
}: {
  name: string
  hint: string
  size: PrintSize
  render: RenderSheet
  onPrint: () => void
}) {
  const sizeLabel = SIZE_LABELS[size]()
  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={onPrint}
        aria-label={m.placeFlyersPrintLabel({ name, size: sizeLabel })}
        className="flex justify-center rounded-lg bg-muted p-4 transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
      >
        {render('pointer-events-none aspect-[148/210] w-full max-w-56 rounded-md shadow-lg')}
      </button>
      <div className="flex flex-col gap-1">
        <p className="font-medium">{name}</p>
        <p className="text-muted-foreground text-sm">{hint}</p>
      </div>
      <Button variant="outline" onClick={onPrint} className="mt-auto">
        <Printer className="mr-2 h-4 w-4" />
        {m.placeFlyersPrint({ size: sizeLabel })}
      </Button>
    </div>
  )
}
